import { useQuery } from '@tanstack/react-query';
import type {
  PreviewPanelRequest,
  PreviewCountertopRequest,
  PreviewDrawerRequest,
  PreviewElementRequest,
  PreviewLightingRequest,
  PreviewCabinetRequest,
  PreviewCabinetPiecesRequest,
} from '@kreart/shared';
import { pricingApi } from './api';

const ROOT = ['pricing-preview'] as const;

/**
 * The engine constants behind the numbers the dialogs explain — today just the
 * glass-sanding markup.
 *
 * Its own query rather than a field on the preview response: the material
 * picker quotes the markup for rows in the dropdown, before anything has been
 * selected and so before any preview exists. Effectively static, so it is
 * cached for the session and shared by every dialog on the page.
 */
export function usePricingConstants(opts: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: ['pricing-constants'] as const,
    queryFn: () => pricingApi.constants(),
    enabled: opts.enabled !== false,
    meta: { silent: true },
    staleTime: Infinity,
    retry: false,
  });
}

interface Options {
  /** When false, the query is disabled (e.g. when required fields are missing). */
  enabled?: boolean;
}

export function usePreviewPanel(body: PreviewPanelRequest | null, opts: Options = {}) {
  return useQuery({
    queryKey: [...ROOT, 'panel', body] as const,
    queryFn: () => pricingApi.previewPanel(body!),
    enabled: !!body && opts.enabled !== false,
    meta: { silent: true },
    staleTime: 30_000,
    retry: false,
  });
}

export function usePreviewCountertop(body: PreviewCountertopRequest | null, opts: Options = {}) {
  return useQuery({
    queryKey: [...ROOT, 'countertop', body] as const,
    queryFn: () => pricingApi.previewCountertop(body!),
    enabled: !!body && opts.enabled !== false,
    meta: { silent: true },
    staleTime: 30_000,
    retry: false,
  });
}

export function usePreviewDrawer(body: PreviewDrawerRequest | null, opts: Options = {}) {
  return useQuery({
    queryKey: [...ROOT, 'drawer', body] as const,
    queryFn: () => pricingApi.previewDrawer(body!),
    enabled: !!body && opts.enabled !== false,
    meta: { silent: true },
    staleTime: 30_000,
    retry: false,
  });
}

export function usePreviewElement(body: PreviewElementRequest | null, opts: Options = {}) {
  return useQuery({
    queryKey: [...ROOT, 'element', body] as const,
    queryFn: () => pricingApi.previewElement(body!),
    enabled: !!body && opts.enabled !== false,
    meta: { silent: true },
    staleTime: 30_000,
    retry: false,
  });
}

export function usePreviewLighting(body: PreviewLightingRequest | null, opts: Options = {}) {
  return useQuery({
    queryKey: [...ROOT, 'lighting', body] as const,
    queryFn: () => pricingApi.previewLighting(body!),
    enabled: !!body && opts.enabled !== false,
    meta: { silent: true },
    staleTime: 30_000,
    retry: false,
  });
}

export function usePreviewCabinet(body: PreviewCabinetRequest | null, opts: Options = {}) {
  return useQuery({
    queryKey: [...ROOT, 'cabinet', body] as const,
    queryFn: () => pricingApi.previewCabinet(body!),
    enabled: !!body && opts.enabled !== false,
    meta: { silent: true },
    staleTime: 30_000,
    retry: false,
  });
}

/** Cut-list ("Pe piese") cabinet preview. Pass `null` to keep the query idle. */
export function usePreviewCabinetPieces(
  body: PreviewCabinetPiecesRequest | null,
  opts: Options = {},
) {
  return useQuery({
    queryKey: [...ROOT, 'cabinet-pieces', body] as const,
    queryFn: () => pricingApi.previewCabinetPieces(body!),
    enabled: !!body && opts.enabled !== false,
    meta: { silent: true },
    staleTime: 30_000,
    retry: false,
  });
}
